import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import moment from 'moment'

// Shared risk-assessment form dialog — used by the patient page
// (co-ordinators), the crew view (on-scene Risk Assessment journeys), and
// the hospital portal (ward/site staff). Callers own persistence via onSave.

export const ratingOptions = [
  'Low',
  'Moderate',
  'High',
  'Critical',
  'Not Assessed',
]
export const overallRiskOptions = ['Low', 'Moderate', 'High', 'Critical']
export const assessmentTypes = [
  'Discharge Home',
  'Pre-Transport',
  'Home Visit',
  'Other',
]
export const safeToDischargeOptions = ['Yes', 'No', 'Conditional', 'Unknown']

export const riskColors = {
  Low: '#067647',
  Moderate: '#B7791F',
  High: '#B42318',
  Critical: '#B42318',
  'Not Assessed': '#52606D',
}

export const sectionDefinitions = [
  ['Access', 'access'],
  ['Home Environment', 'homeEnvironment'],
  ['Moving and Handling', 'movingHandling'],
  ['Cognition and Communication', 'cognitionCommunication'],
  ['Clinical Care', 'clinicalCare'],
  ['Infection Control', 'infectionControl'],
  ['Safeguarding', 'safeguarding'],
  ['Transport', 'transport'],
]

const emptySections = sectionDefinitions.reduce((sections, [, key]) => {
  sections[key] = { rating: 'Not Assessed', notes: '' }
  return sections
}, {})

export const getDefaultAssessment = (patient = {}) => ({
  assessmentType: 'Discharge Home',
  visitDate: moment().format('YYYY-MM-DDTHH:mm'),
  attendees: '',
  summary: '',
  sections: JSON.parse(JSON.stringify(emptySections)),
  transportRequirements: {
    mobilityCategory: patient?.mobility?.category || '',
    crewRequirements: '',
    vehicleRequirements: '',
    equipment: '',
    escortRequired: false,
    escortDetails: '',
    oxygenRequired: false,
    oxygenDetails: '',
    bariatricConsiderations: '',
  },
  dischargeRecommendations: {
    safeToDischargeHome: 'Unknown',
    recommendedPathway: '',
    hospitalTeamNotes: '',
    communitySupportRequired: '',
  },
  actions: [],
  overallRisk: 'Moderate',
  status: 'Completed',
  hospitalVisible: true,
})

const setNestedValue = (object, path, value) => {
  const keys = path.split('.')
  const clone = { ...object }
  let current = clone
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value
    } else {
      current[key] = { ...(current[key] || {}) }
      current = current[key]
    }
  })
  return clone
}

const formatDateForInput = (date) =>
  date ? moment(date).format('YYYY-MM-DDTHH:mm') : ''

export const normalizeAssessmentForSave = (assessment) => ({
  ...assessment,
  visitDate: assessment.visitDate ? new Date(assessment.visitDate) : undefined,
  transportRequirements: {
    ...assessment.transportRequirements,
    equipment: String(assessment.transportRequirements?.equipment || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
  },
  actions: (assessment.actions || [])
    .filter((action) => action.action)
    .map((action) => ({
      ...action,
      dueDate: action.dueDate ? new Date(action.dueDate) : undefined,
    })),
})

export const RiskChip = ({ risk }) => (
  <Chip
    size="small"
    label={risk || 'Not Set'}
    sx={{
      bgcolor: `${riskColors[risk] || '#52606D'}18`,
      color: riskColors[risk] || '#52606D',
      border: `1px solid ${riskColors[risk] || '#52606D'}40`,
      fontWeight: 800,
      borderRadius: 1,
    }}
  />
)

const RiskAssessmentDialog = ({
  open,
  onClose,
  onSave,
  patient,
  selectedAssessment,
  saving,
  defaultAssessmentType = null,
  hideHospitalVisibility = false,
}) => {
  const [form, setForm] = useState(getDefaultAssessment(patient))

  useEffect(() => {
    if (!open) return
    setForm(
      selectedAssessment
        ? {
            ...getDefaultAssessment(patient),
            ...selectedAssessment,
            visitDate: formatDateForInput(selectedAssessment.visitDate),
            transportRequirements: {
              ...getDefaultAssessment(patient).transportRequirements,
              ...selectedAssessment.transportRequirements,
              equipment: (
                selectedAssessment.transportRequirements?.equipment || []
              ).join(', '),
            },
          }
        : {
            ...getDefaultAssessment(patient),
            ...(defaultAssessmentType
              ? { assessmentType: defaultAssessmentType }
              : {}),
          },
    )
  }, [open, selectedAssessment, patient])

  const update = (path, value) =>
    setForm((prev) => setNestedValue(prev, path, value))

  const updateAction = (index, path, value) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, [path]: value } : action,
      ),
    }))
  }

  const addAction = () => {
    setForm((prev) => ({
      ...prev,
      actions: [
        ...(prev.actions || []),
        { action: '', owner: '', dueDate: '' },
      ],
    }))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {selectedAssessment ? 'Edit Risk Assessment' : 'New Risk Assessment'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Assessment Type"
                  value={form.assessmentType}
                  onChange={(e) => update('assessmentType', e.target.value)}
                >
                  {assessmentTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Assessment Date"
                  type="datetime-local"
                  value={form.visitDate || ''}
                  onChange={(e) => update('visitDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Overall Risk"
                  value={form.overallRisk}
                  onChange={(e) => update('overallRisk', e.target.value)}
                >
                  {overallRiskOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Safe to Discharge"
                  value={form.dischargeRecommendations.safeToDischargeHome}
                  onChange={(e) =>
                    update(
                      'dischargeRecommendations.safeToDischargeHome',
                      e.target.value,
                    )
                  }
                >
                  {safeToDischargeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Executive Summary"
            value={form.summary}
            onChange={(e) => update('summary', e.target.value)}
          />
          <Box>
            <Grid container spacing={2}>
              {sectionDefinitions.map(([label, key]) => (
                <Grid item xs={12} md={6} key={key}>
                  <Stack
                    spacing={1.25}
                    sx={{ p: 1.5, border: '1px solid #E2E8F0' }}
                  >
                    <Typography fontWeight={900}>{label}</Typography>
                    <TextField
                      select
                      label="Risk Rating"
                      value={form.sections?.[key]?.rating || 'Not Assessed'}
                      onChange={(e) =>
                        update(`sections.${key}.rating`, e.target.value)
                      }
                    >
                      {ratingOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      multiline
                      minRows={2}
                      label="Findings and Controls"
                      value={form.sections?.[key]?.notes || ''}
                      onChange={(e) =>
                        update(`sections.${key}.notes`, e.target.value)
                      }
                    />
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Divider />
          <Typography fontWeight={950}>Transport Requirements</Typography>
          <Box>
            {' '}
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Mobility Category"
                  value={form.transportRequirements.mobilityCategory}
                  onChange={(e) =>
                    update(
                      'transportRequirements.mobilityCategory',
                      e.target.value,
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Crew Requirements"
                  value={form.transportRequirements.crewRequirements}
                  onChange={(e) =>
                    update(
                      'transportRequirements.crewRequirements',
                      e.target.value,
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Vehicle Requirements"
                  value={form.transportRequirements.vehicleRequirements}
                  onChange={(e) =>
                    update(
                      'transportRequirements.vehicleRequirements',
                      e.target.value,
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Equipment"
                  value={form.transportRequirements.equipment}
                  onChange={(e) =>
                    update('transportRequirements.equipment', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.transportRequirements.escortRequired}
                      onChange={(e) =>
                        update(
                          'transportRequirements.escortRequired',
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Escort Required"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.transportRequirements.oxygenRequired}
                      onChange={(e) =>
                        update(
                          'transportRequirements.oxygenRequired',
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Oxygen Required"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bariatric Considerations"
                  value={form.transportRequirements.bariatricConsiderations}
                  onChange={(e) =>
                    update(
                      'transportRequirements.bariatricConsiderations',
                      e.target.value,
                    )
                  }
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />
          <Typography fontWeight={950}>Discharge Team Notes</Typography>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Recommended Pathway"
                  value={form.dischargeRecommendations.recommendedPathway}
                  onChange={(e) =>
                    update(
                      'dischargeRecommendations.recommendedPathway',
                      e.target.value,
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Community Support Required"
                  value={form.dischargeRecommendations.communitySupportRequired}
                  onChange={(e) =>
                    update(
                      'dischargeRecommendations.communitySupportRequired',
                      e.target.value,
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Hospital Team Notes"
                  value={form.dischargeRecommendations.hospitalTeamNotes}
                  onChange={(e) =>
                    update(
                      'dischargeRecommendations.hospitalTeamNotes',
                      e.target.value,
                    )
                  }
                />
              </Grid>
            </Grid>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography fontWeight={950}>Actions</Typography>
            <Button startIcon={<AddIcon />} onClick={addAction}>
              Add Action
            </Button>
          </Stack>
          {(form.actions || []).map((action, index) => (
            <Grid container spacing={1.5} key={index}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Action"
                  value={action.action}
                  onChange={(e) =>
                    updateAction(index, 'action', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Owner"
                  value={action.owner}
                  onChange={(e) => updateAction(index, 'owner', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Due Date"
                  value={
                    action.dueDate
                      ? moment(action.dueDate).format('YYYY-MM-DD')
                      : ''
                  }
                  onChange={(e) =>
                    updateAction(index, 'dueDate', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          ))}

          {!hideHospitalVisibility && (
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.hospitalVisible}
                  onChange={(e) => update('hospitalVisible', e.target.checked)}
                />
              }
              label="Visible in hospital portal"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave(normalizeAssessmentForSave(form))}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Assessment'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RiskAssessmentDialog
